import {
  FormControl,
  FormControlLabel,
  Radio as MuiRadio,
  RadioGroup as MuiRadioGroup,
  type SxProps,
} from '@mui/material';
import { Typography, useTheme } from 'hephaestus/web';
import type { SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldLabel, useFieldIds } from '../fieldLabel/fieldLabel';
import { HelpText } from '../helpText/helpText';
import { useHelpTextTokens } from '../helpText/helpText.tokens';
import { useRadioTokens } from './radio.tokens';

type RadioFunctionalProps = {
  value: string | number;
  onChange?: (event: SyntheticEvent<Element, Event>, checked: boolean) => void;
  disabled?: boolean;
};
type RadioVisualProps = {
  label: React.ReactNode;
  subLabel?: string;
  size?: 'medium' | 'small';
  testId?: string;
};

export const Radio = (props: RadioFunctionalProps & RadioVisualProps) => {
  const theme = useTheme();
  const tokens = useRadioTokens(theme);

  const fontSizes = {
    small: '14px',
    medium: '16px',
  };
  const lineHeights = {
    small: '20px',
    medium: '24px',
  };
  return (
    <>
      <FormControlLabel
        value={props.value}
        onChange={props.onChange}
        disabled={props.disabled}
        label={props.label}
        disableTypography
        sx={{
          fontFamily:
            tokens.fontFamily ||
            'Interphases Pro, "Causten Round", Helvetica, sans-serif',
          fontSize: fontSizes[props.size ?? 'medium'],
          lineHeight: lineHeights[props.size ?? 'medium'],
          fontWeight: 400,
          color: !props.disabled ? tokens.defaultColor : tokens.disabledColor,
        }}
        control={
          <MuiRadio
            data-testid={props.testId}
            size={props.size}
            inputProps={
              {
                'data-testid': `${props.testId ?? 'radio'}-input`,
              } as React.InputHTMLAttributes<HTMLInputElement>
            }
            sx={{
              '&.MuiRadio-root': {
                color: tokens.defaultColor,
                '&.Mui-checked': { color: tokens.activeColor },
                '&.Mui-disabled': { color: tokens.disabledColor },
              },
            }}
          />
        }
      />
      {props.subLabel && (
        <Typography
          variant='bodyMedium'
          onClick={(e: any) => props.onChange && props.onChange(e, true)}
          sx={{
            color: tokens.subLabelColor,
            ml: 4,
            mt: -1,
            cursor: 'pointer',
            mb: 1,
            fontWeight: 500,
          }}
        >
          {props.subLabel}
        </Typography>
      )}
    </>
  );
};

type RadioGroupProps = {
  id?: string;
  name?: string;
  label?: string;
  placeholder?: string;
  helpText?: string;
  error?: boolean;
  errorText?: string;
  success?: boolean;
  successText?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  sx?: SxProps;
  children?: React.ReactNode[] | React.ReactNode;
  value?: string | number;
  onChange?: any;
  onFocus?: any;
  infoTooltip?: string;
  testId?: string;
  required?: boolean;
};

export const RadioGroup = ({
  id,
  name,
  label,
  helpText,
  error = false,
  errorText,
  success = false,
  successText,
  fullWidth,
  disabled = false,
  sx,
  onChange,
  value,
  children,
  infoTooltip,
  testId,
  required,
}: RadioGroupProps) => {
  const helpTextTokens = useHelpTextTokens(useTheme());
  const { t } = useTranslation('components');
  // Create a key that changes when value changes from undefined to defined
  // so that the component can be re-rendered when one of the options is selected for the first time.
  const radioGroupKey =
    value !== undefined ? 'defined-value' : 'undefined-value';

  const { inputId, labelId } = useFieldIds(id);
  const labelledBy = label ? labelId : undefined;
  const shouldDisplayInDialog = helpTextTokens.displayInDialog;
  const helpTextToShow =
    infoTooltip && !shouldDisplayInDialog ? infoTooltip : helpText;

  const helpTextFunc = (isError: boolean, isSuccess: boolean) => {
    if (isError) {
      return (
        (errorText ?? t('fieldErrors.default', { fieldName: label })) ||
        helpTextToShow
      );
    }

    if (isSuccess) {
      return successText || helpTextToShow;
    }

    return helpTextToShow;
  };

  return (
    <FormControl
      data-testid={testId ?? 'selection-tile'}
      sx={{ ...sx, width: fullWidth ? '100%' : 'default' }}
      disabled={disabled}
    >
      {label ? (
        <FieldLabel
          id={labelledBy}
          error={error}
          required={required}
          infoTooltip={infoTooltip}
          shouldDisplayInDialog={shouldDisplayInDialog}
        >
          {label}
        </FieldLabel>
      ) : null}
      <MuiRadioGroup
        row
        id={inputId}
        key={radioGroupKey}
        aria-labelledby={labelledBy}
        name={name ?? inputId}
        value={value}
        onChange={onChange}
      >
        {children}
      </MuiRadioGroup>
      {helpTextFunc(error, success) && (
        <HelpText
          text={helpTextFunc(error, success)}
          tone={error ? 'error' : success ? 'success' : 'neutral'}
        />
      )}
    </FormControl>
  );
};
